import type {
  ExtractParameters,
  HttpRequestMethod,
} from "core/entities/UtilityTypes";
import { templateRoute } from "./templateRoute";

export function buildUrl<
  Paths extends Record<string, any>,
  P extends keyof Paths,
  M extends HttpRequestMethod = "get",
>({
  path: originalPath,
  parameters,
  locale,
  baseUrl,
}: {
  path: P;
  parameters: ExtractParameters<Paths, P, M> | undefined;
  locale?: string;
  baseUrl: string;
}) {
  let path: string = originalPath as string;
  const searchParams = new URLSearchParams();
  // Not setting via headers as the headers are not used by SWR for the storage key
  // https://aspnetboilerplate.com/Pages/Documents/Localization#how-the-current-language-is-determined
  if (locale) {
    searchParams.set("culture", locale);
    searchParams.set("ui-culture", locale);
  }

  // if no parameters are provided, return the URL with the locale
  if (!parameters || typeof parameters !== "object") {
    const url = new URL(path, baseUrl);
    url.search = searchParams.toString();
    return url.toString();
  }

  // replace path parameters
  if (
    "path" in parameters &&
    typeof parameters.path === "object" &&
    parameters.path
  ) {
    path = templateRoute(path, parameters.path as Record<string, string>);
  }

  // add query parameters
  if ("query" in parameters && parameters.query) {
    Object.entries(parameters.query)
      .sort((a, b) => (a[0] > b[0] ? 1 : -1))
      .forEach(([key, value]) => {
        if (value === undefined) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((v) => {
            searchParams.append(key, v);
          });
          return;
        }
        searchParams.append(key, value);
      });
  }

  const url = new URL(path, baseUrl);
  url.search = searchParams.toString();
  return url.toString();
}
